/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import { isReactNativeWrappedApp } from '../../../../util/reactnativeAppHelper';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};
/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    customLinks,
    onLogout,
  } = props;
  const intl = useIntl();
  const user = ensureCurrentUser(currentUser);
  const {
    publicData,
    customUserType = publicData?.customUserType,
    isPremiumVendor = publicData?.isPremiumVendor,
  } = user?.attributes?.profile || {};
  const isVendor = customUserType === 'vendor';
  const listingButtonLabel = intl.formatMessage({
    id: isVendor
      ? 'TopbarMobileMenu.listYourProductsButtonText'
      : 'TopbarMobileMenu.postForQuotesButtonText',
  });

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  // if (!isAuthenticated) {
  //   const signup = (
  //     <NamedLink name="SignupPage" className={css.signupLink}>
  //       <FormattedMessage id="TopbarMobileMenu.signupLink" />
  //     </NamedLink>
  //   );

  //   const login = (
  //     <NamedLink name="LoginPage" className={css.loginLink}>
  //       <FormattedMessage id="TopbarMobileMenu.loginLink" />
  //     </NamedLink>
  //   );

  //   const signupOrLogin = (
  //     <span className={css.authenticationLinks}>
  //       <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
  //     </span>
  //   );

  //   return (
  //     <div className={css.root}>
  //       <div className={css.content}>
  //         <div className={css.authenticationGreeting}>
  //           <FormattedMessage
  //             id="TopbarMobileMenu.unauthorizedGreeting"
  //             values={{ lineBreak: <br />, signupOrLogin }}
  //           />
  //         </div>
  //         <div className={css.customLinksWrapper}>{extraLinks}</div>
  //         <div className={css.spacer} />
  //       </div>
  //       <div className={css.footer}>
  //         <NamedLink className={css.createNewListingLink} name="NewListingPage">
  //           <FormattedMessage id="TopbarMobileMenu.newListingLink" />
  //         </NamedLink>
  //       </div>
  //     </div>
  //   );
  // }

  if (!isAuthenticated) {
    const signupAsVendor = (
      <NamedLink name="SignupAsVendorPage" className={css.signupLink}>
        <FormattedMessage id="TopbarDesktop.signupAsVendor" />
      </NamedLink>
    );

    const signupAsCustomer = (
      <NamedLink name="SignupAsCustomerPage" className={css.signupLink}>
        <FormattedMessage id="TopbarDesktop.signupAsCustomer" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage
          id="TopbarMobileMenu.signupOrLoginCustom"
          values={{ signupAsVendor, signupAsCustomer, login }}
        />
      </span>
    );

    return (
      <div className={css.root}>
        <div
          className={classNames(css.contentCustom, {
            [css.appContentSection]: isReactNativeWrappedApp(),
          })}
        >
          <div
            className={classNames(css.upperSection, {
              [css.appUpperSection]: isReactNativeWrappedApp(),
            })}
          >
            <div>
              <NamedLink name="LandingPage">
                <FormattedMessage id="TopbarMobileMenu.landingPageLink" />
              </NamedLink>
            </div>
            <div>
              <NamedLink name="PricingPage" className={css.pricingButton}>
                <FormattedMessage id="TopbarDesktop.pricing" />
              </NamedLink>
            </div>
            {isReactNativeWrappedApp() ? (
              <React.Fragment>
                <div>
                  <NamedLink name="CMSPage" params={{ pageId: 'about' }}>
                    <FormattedMessage id="TopbarMobileMenu.aboutUsLink" />
                  </NamedLink>
                </div>
                <div>
                  <ExternalLink href="mailto:admin@bizzprofiles.com">
                    <FormattedMessage id="TopbarMobileMenu.contactUsLink" />
                  </ExternalLink>
                </div>
                <div>
                  <NamedLink name="TermsOfServicePage">
                    <FormattedMessage id="TopbarMobileMenu.termOfServiceLink" />
                  </NamedLink>
                </div>
                <div>
                  <NamedLink name="PrivacyPolicyPage">
                    <FormattedMessage id="TopbarMobileMenu.privacyPolicyLink" />
                  </NamedLink>
                </div>
              </React.Fragment>
            ) : null}

            <div>
              <NamedLink name="LoginPage">
                <FormattedMessage id="TopbarMobileMenu.loginLink" />
              </NamedLink>
            </div>
            {isReactNativeWrappedApp() ? (
              <React.Fragment>
                <div>
                  <NamedLink name="SignupAsVendorPage">
                    <FormattedMessage id="TopbarDesktop.signupAsVendor" />
                  </NamedLink>
                </div>

                <div>
                  <NamedLink name="SignupAsCustomerPage">
                    <FormattedMessage id="TopbarDesktop.signupAsCustomer" />
                  </NamedLink>
                </div>
              </React.Fragment>
            ) : null}
          </div>
          {isReactNativeWrappedApp() ? null : (
            <div className={css.lowerSection}>
              <div>
                <NamedLink name="SignupAsVendorPage">
                  <FormattedMessage id="TopbarDesktop.signupAsVendor" />
                </NamedLink>
              </div>

              <div>
                <NamedLink name="SignupAsCustomerPage">
                  <FormattedMessage id="TopbarDesktop.signupAsCustomer" />
                </NamedLink>
              </div>
            </div>
          )}

          {/* <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>
          <div className={css.customLinksWrapper}>{extraLinks}</div>
          <div className={css.spacer} /> */}
        </div>

        <div className={css.footer}>
          <div>
            <NamedLink className={css.createNewListingLink} name="SignupAsVendorPage">
              <FormattedMessage id="TopbarMobileMenu.listYourProductsButtonText" />
            </NamedLink>
          </div>

          <div>
            <NamedLink className={css.createNewRequestLink} name="SignupAsCustomerPage">
              <FormattedMessage id="TopbarMobileMenu.postForQuotesButtonText" />
            </NamedLink>
          </div>
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const inboxTab = currentUserHasListings ? 'orders' : 'sales';

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  return (
    <div className={css.root}>
      <div className={css.content}>
        <AvatarLarge
          className={classNames(css.avatar, { [css.appAvatar]: isReactNativeWrappedApp() })}
          user={currentUser}
        />

        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>

        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <div className={css.accountLinksWrapper}>
          <NamedLink
            name="PricingPage"
            className={classNames(css.inbox, currentPageClass('PricingPage'))}
          >
            <FormattedMessage id="TopbarDesktop.pricing" />
          </NamedLink>

          <NamedLink
            className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>

          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            {isVendor ? (
              <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
            ) : (
              <FormattedMessage id="TopbarMobileMenu.yourRFQsLink" />
            )}
          </NamedLink>

          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>

          {isVendor && isPremiumVendor && (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('RaisedQuotes'))}
              name="RaisedQuotes"
            >
              <FormattedMessage id="TopbarMobileMenu.raisedQuotesLink" />
            </NamedLink>
          )}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('FavouritesPage'))}
            name="FavouritesPage"
          >
            <FormattedMessage id="TopbarMobileMenu.favouritesLink" />
          </NamedLink>
          {isReactNativeWrappedApp() ? (
            <React.Fragment>
              {' '}
              <NamedLink
                name="CMSPage"
                params={{ pageId: 'about' }}
                className={classNames(css.navigationLink, currentPageClass('CMSPage:about'))}
              >
                <FormattedMessage id="TopbarMobileMenu.aboutUsLink" />
              </NamedLink>
              <ExternalLink
                href="mailto:admin@bizzprofiles.com"
                className={classNames(css.navigationLink)}
              >
                <FormattedMessage id="TopbarMobileMenu.contactUsLink" />
              </ExternalLink>
              <NamedLink
                name="TermsOfServicePage"
                className={classNames(css.navigationLink, currentPageClass('TermsOfServicePage'))}
              >
                <FormattedMessage id="TopbarMobileMenu.termOfServiceLink" />
              </NamedLink>
              <NamedLink
                name="PrivacyPolicyPage"
                className={classNames(css.navigationLink, currentPageClass('PrivacyPolicyPage'))}
              >
                <FormattedMessage id="TopbarMobileMenu.privacyPolicyLink" />
              </NamedLink>
            </React.Fragment>
          ) : null}
        </div>

        <div className={css.customLinksWrapper}>{extraLinks}</div>

        <div className={css.spacer} />
      </div>

      <div className={css.footer}>
        <NamedLink className={css.createNewListingLink} name="NewListingPage">
          {listingButtonLabel}
        </NamedLink>
      </div>
    </div>
  );
};

export default TopbarMobileMenu;
